import { useDataGrid } from "@/hooks/useDataGrid";
import { Checkbox, TableContainer, Tbody, Td, Text, Th, Tr } from "@chakra-ui/react";
import { ChangeEvent, MouseEvent, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ResponseNewType } from "../types";
import { TableSkeleton } from "./TableSkeleton";
import { TableElement, TableHeading } from "./styles";
import { IconTag } from "@/components/Icons";

interface Props {
  idKey?: string;
  allElement?: boolean;
  headers: {
    key: string;
    title: string;
    filter?: (value: any) => string;
    iconTag?: boolean;
    hasKey?: boolean;
  }[];
  selectable?: boolean;
  deletable?: boolean;
  editable?: boolean;
  canOpen?: boolean;
  onDataLoaded?: (data: any[]) => void;
}

export function Table<DataType>({
  selectable = true,
  editable = true,
  headers,
  idKey = "id",
  allElement = false,
  canOpen = false,
  onDataLoaded,
}: Props) {

  const [allSelected, setAllSelected] = useState<boolean>(false);
  const { selecteds, setSelecteds, queryResult } = useDataGrid();
  const navigate = useNavigate();


  if (!queryResult || queryResult.isLoading || !queryResult.data) {
    return <TableSkeleton />;
  }


  function onRowClick(id: string) {
    return (e: MouseEvent<HTMLElement>) => {
      if (!editable && !canOpen) return null;
      if (e.currentTarget.nodeName !== "TR") return;
      navigate(`${id}`);
    };
  }

  function toggleSelectAll(e: ChangeEvent<HTMLInputElement>) {
    if (allSelected) {
      setSelecteds([]);
      setAllSelected(false);
    } else {
      allElement ?
        setSelecteds(
          (paginacao || data).map((item: any) => item)
        ) :
        setSelecteds(
          (paginacao || data).map((item: any) => item[idKey as keyof DataType] as string)
        )
      setAllSelected(true);
    }
  }

  function toggleItem(id: string | any) {
    return () => {
      //@ts-ignore
      if (selecteds.find((_id) => allElement ? id?.[idKey] === _id?.[idKey] : id === _id)) {
        setSelecteds(selecteds.filter((_id) => id !== _id));
      } else {
        setSelecteds([...selecteds, id]);
      }

      if (allSelected && selecteds.length < 2) {
        setAllSelected(false);
      }

      if (selecteds.length + 1 === data.length) {
        setAllSelected(true);
      }
    };
  }


  function applyFilter(
    filter: undefined | ((v: any) => string),
    value: any
  ): string {
    if (typeof filter === "function") {
      return filter(value);
    }

    return value;
  }


  const {
    /* @ts-ignore */
    data: { paginacao }
    //@ts-ignore
  } = queryResult?.data as ResponseNewType<DataType>;

  const {
    data
  } = queryResult?.data as ResponseNewType<DataType>;

  if (data && onDataLoaded) {
    onDataLoaded(data);
  }


  const Rows = (() => {
    if (!!(paginacao || data)?.length) {
      return (paginacao || data)?.map(
        (item: DataType, row: number) => (
          <Tr
            title="row-table"
            key={row}
            bg={
              (row % 2 ? '#f8f8f8' : 'inherit')
            }
            sx={{
              "&  [data-tag]": {
                display: 'none',
              }
            }}
            _hover={{
              "&": {
                cursor: 'pointer',
              },
              "& > th > div > .iconOverlay": {
                display: 'flex',
                background: '#cccccc',
                borderRadius: '8px'
              },
              "&  [data-tag]": {
                display: 'block'
              }
            }}

            onClick={headers?.some(head => head.hasKey) ? onRowClick((headers?.find(head => head.hasKey)?.key?.split(".").reduce((acc: any, curr: string) => acc[curr], item)) || item[idKey as keyof DataType] as string) : onRowClick(item[idKey as keyof DataType] as string)}
          >
            {
              selectable && (
                <Td align="center" position={'sticky'} zIndex={1} left={0} bg={row % 2 ? '#f8f8f8' : 'white'} onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    isChecked={
                      !!selecteds.find(
                        /* @ts-ignore */
                        (id) => allElement ? item[idKey as keyof DataType] === id[idKey as keyof DataType] : item[idKey as keyof DataType] === id
                      )
                    }
                    size={'md'}
                    borderColor={'gray.500'}
                    onChange={toggleItem(
                      allElement ? item :
                        item[idKey as keyof DataType] as string
                    )}
                  />
                </Td>
              )}
            {
              headers.map(
                ({ key }, column) => (
                  <Td
                    key={`${row}-${column}`}
                    sx={{
                      maxWidth: !canOpen && headers[column]?.iconTag ? '60px' : 'max-content',
                      wordBreak: 'keep-all',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                    height={'40px'}
                    fontSize={12}
                  >

                    <div style={{
                      display: 'flex',
                      overflowX: 'hidden',
                      width: '100%',
                      textOverflow: 'ellipsis',
                      alignItems: 'center',
                      gap: 4
                    }}>
                      <p style={{
                        textOverflow: 'ellipsis',
                        overflowX: 'hidden',
                      }}>
                        {
                          applyFilter(
                            headers[column].filter,
                            key
                              .split(".")
                              .reduce((acc: any, curr: string) => acc[curr], item)
                          )
                        }
                      </p>
                      {
                        !canOpen && headers[column]?.iconTag && (
                          <div style={{ flexGrow: 1 }}>
                            <IconTag />
                          </div>
                        )
                      }
                    </div>
                  </Td>
                )
              )
            }
          </Tr>
        )
      )
    }
  })

  return (
    <>
      <TableContainer
        position={'relative'}
        transition={'ease-in all 150ms'}
        borderTop={'1px solid #dbdbdb'}
        bg={'#fff'}
        overflowY={'auto'}
        maxH={'calc(100vh - 30vh)'}
        aria-describedby="table-container"
        boxShadow={'3px 2px 5px rgba(100,100,100,.1)'}
      >
        <TableElement>

          <TableHeading >
            <Tr style={{ background: '#f8f8f8' }}>
              {
                selectable && (
                  <Th width={10}>
                    <Checkbox
                      isIndeterminate={
                        !!selecteds?.length
                      }
                      borderColor={'gray.500'}
                      isChecked={false}
                      onChange={toggleSelectAll}
                    />
                  </Th>
                )
              }
              {
                headers.map(
                  ({ title }, index) => (
                    <Th key={index} fontSize={14} fontWeight={'bold'}>{title}</Th>
                  )
                )
              }
            </Tr>
          </TableHeading>
          <Tbody >
            {
              !(paginacao || data).length && (
                <Tr>
                  <Td colSpan={headers.length + 1}>
                    <Text p={10} textAlign="center">
                      Nenhum item para exibir
                    </Text>
                  </Td>
                </Tr>
              )
            }

            {Rows()}
          </Tbody>
        </TableElement>
      </TableContainer>
    </>
  );
}
